// The standalone SEO pages — six stage/service/local pages plus two
// authority pages merged in from authorityPages.js. Each is a genuinely useful resource in
// Andrea's kitchen-table voice — recognition first, discovery before
// diagnosis, teach before the invitation. No prices, no keyword stuffing,
// no exaggerated promises. The Free Sleep Assessment stays the next step
// everywhere, with the text option beside it.

import { AUTHORITY_PAGES } from "./authorityPages";

export const SLEEP_PAGES = {
  "newborn-sleep-consultant": {
    path: "/newborn-sleep-consultant",
    meta: {
      title: "Newborn Sleep Consultant & Support (0–3 Months) | The Nurtured Parent Co.",
      description:
        "A newborn sleep consultant for the first three months — what's actually normal, day/night confusion, contact sleep, short naps and frequent waking. Gentle foundations, not sleep training. Start with a free sleep assessment.",
    },
    label: "Newborn sleep · 0–3 months",
    crumb: "Newborn sleep",
    h1: ["Newborn sleep consultant —", "and support for the", "first few months."],
    hero: [
      "Right now, sleep probably looks like this: your baby sleeps beautifully on you — and wakes the minute they touch the crib. Naps end at 32 minutes. Nights blur into feeds. Someone told you to put them down \u201cdrowsy but awake,\u201d and you're still not sure what that's supposed to look like at three in the morning.",
      "Okay. First — nothing about this means you've done something wrong. You're looking at a newborn. Newborn sleep is genuinely different from everything that comes after, which is exactly why most of the sleep advice out there doesn't fit these weeks.",
    ],
    sections: [
      {
        h2: "What newborn sleep actually looks like",
        body: [
          "Newborns sleep in short cycles — often 45 to 60 minutes — and they spend much more of that time in light, active sleep than older babies do. That's why they startle, grunt, squirm and wake so easily. It isn't a flaw. It's wiring.",
          "Day and night confusion is common in the early weeks: a long stretch at two in the afternoon, wide eyes at two in the morning. Their internal clock simply hasn't been built yet — it develops across these months, and there are gentle ways to help it along.",
          "Frequent night feeding is normal and necessary. Short naps are normal. Wanting to sleep on you is normal. None of these are habits you're creating. They're where you start from.",
        ],
      },
      {
        h2: "What support means at this age",
        body: [
          "Support for a newborn is not infant sleep training. Nobody should be \u201ctraining\u201d a six-week-old — and I won't suggest it.",
          "What we do instead is quieter. We look at what your baby's days and nights actually look like, and we build gentle early foundations: helping day feel like day and night feel like night, protecting the sleep that's already happening, and giving you a few settling options for the hard moments — nothing that doesn't feel right to you.",
        ],
        andrea:
          "The goal in these months isn't perfect sleep. It's understanding what's normal, supporting what's developing, and making your nights a little more predictable — at your baby's pace.",
      },
      {
        h2: "What I'd want to know first",
        list: [
          "How does your baby fall asleep right now — and what happens when you put them down?",
          "What do nights look like, feed by feed?",
          "What did today look like before tonight?",
          "What's the hardest moment of the day, for you?",
          "What have you already tried — and did any of it work, even for a little while?",
        ],
        body: ["Those details matter more to me than any schedule I could hand you."],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "Some of this stage is simply waiting for development to do its work. Some of it genuinely responds to small, early adjustments. The hard part is knowing which is which when you're in the middle of it.",
          "That's what I'm for. And if anything ever feels medically off — feeding struggles, weight gain, breathing that concerns you — your pediatrician comes first, always. I'll say that plainly.",
        ],
      },
    ],
    faq: [
      {
        q: "Is this sleep training for newborns?",
        a: "No. Newborn support is gentle and developmental — understanding what's normal, building early rhythms, and finding settling approaches that feel right to you. Conventional sleep training comes much later, if and when it ever fits your family.",
      },
      {
        q: "My baby only sleeps on me. Is that a problem?",
        a: "It's one of the most normal things a newborn can do. Contact sleep is biology, not a bad habit. We work with it — and gently widen your options over time.",
      },
      {
        q: "When will nights get longer?",
        a: "There's a wide normal range, and it varies baby to baby. What I can tell you is what tends to help it along — and what your baby's current patterns are actually telling us.",
      },
    ],
    related: [
      { href: "/sleep-guides/newborn-day-night-confusion", label: "Guide: why your newborn has days and nights mixed up" },
      { href: "/sleep-guides/newborn-only-sleeps-on-me", label: "Guide: why your newborn only sleeps on you" },
      { href: "/baby-sleep-consultant", label: "What comes next: baby sleep (4–17 months)" },
      { href: "/virtual-sleep-consultant", label: "How virtual sleep support works" },
      { href: "/denver-sleep-consultant", label: "Working together in the Denver area" },
    ],
  },

  "baby-sleep-consultant": {
    path: "/baby-sleep-consultant",
    meta: {
      title: "Baby Sleep Consultant for Infants (4–17 Months) | The Nurtured Parent Co.",
      description:
        "A baby sleep consultant for infants 4–17 months — frequent night waking, short naps, waking the moment they touch the crib, bedtime that keeps getting longer. Understand why it's happening. Start with a free sleep assessment.",
    },
    label: "Infant sleep · 4–17 months",
    crumb: "Baby & infant sleep",
    h1: ["Baby sleep consultant —", "for infants who are", "struggling with sleep."],
    hero: [
      "Maybe it goes like this. Your baby falls asleep in your arms — warm, heavy, finally still — and wakes the minute they touch the crib. So you start over. And the whole thing takes an hour.",
      "Or nights were fine, and now they're up every 90 minutes. Or every nap ends at half an hour, like clockwork, and you spend the rest of the day doing math about what that means for bedtime.",
      "Something shifted. Let's look at what.",
    ],
    sections: [
      {
        h2: "Why sleep changes during these months",
        body: [
          "Somewhere around four months, your baby's sleep cycles mature into something closer to yours — which means the moments between cycles become visible. A baby who drifted through them before now surfaces, notices where they are, and needs the same conditions back to cross into the next cycle.",
          "That's why the transfer suddenly fails. Why the thing that worked two weeks ago stopped. The way your baby falls asleep at bedtime is usually the way they'll ask to fall back asleep at 2 a.m. — so bedtime isn't just bedtime. It's the rehearsal for the whole night.",
          "None of this means you caused it. It means your baby developed — and the plan needs to develop with them. That's the difference a one-on-one infant sleep consultant makes: the plan starts from your baby's actual pattern, not a chart's.",
        ],
      },
      {
        h2: "The questions I'd ask you",
        list: [
          "What happens when you put them down?",
          "How are they falling asleep at bedtime — and what happens when they wake?",
          "What did naps look like that day?",
          "When did this start — and did anything change around the same time?",
          "What have you already tried, and did any of it work, even for a little while?",
        ],
        body: [
          "Sometimes we start talking about bedtime and realize the part worth looking at happened four hours earlier. That's normal. Sleep is one long chain, and the link that matters isn't always the one that's loudest.",
        ],
      },
      {
        h2: "How I'd approach it",
        body: [
          "First I listen — I want to understand why this is happening before anyone changes anything. Then we look at the whole day together, figure out which part actually matters, and start there. One part at a time.",
          "What I won't do is hand you a generic schedule and disappear. A plan for your baby gets built from your baby's patterns, your family's days, and what you're genuinely comfortable doing at 2 a.m. — because a plan only works if you can live with it on the hard night, not just the good one.",
        ],
      },
      {
        h2: "What actually changes",
        list: [
          "You put them down — and they stay asleep.",
          "The 32-minute nap starts becoming a real one.",
          "A night waking gets an answer instead of a debate.",
          "And when something shifts — a tooth, a nap change, a weird week — you understand your baby's sleep well enough that you're not starting over.",
        ],
        body: [
          "That's the real goal. Not just a baby who sleeps — a parent who knows what their baby needs and what to do about it.",
        ],
      },
    ],
    faq: [
      {
        q: "Does this mean cry-it-out?",
        a: "No. I don't fit families into a predetermined method. What I suggest depends on your baby's age, development and temperament, and what you're comfortable actually doing night after night. You'll know your options — and the decision is yours.",
      },
      {
        q: "We already tried a schedule and it didn't work.",
        a: "That's common, and it's useful information. A schedule tells you when. It can't tell you why your baby wakes — and the why is usually the part that's actually in the way. Bring what you tried; even the failures tell us something.",
      },
      {
        q: "Is 4–17 months the right age to get help?",
        a: "It's the age range where sleep patterns become changeable in a real way — but there's no wrong time inside it. What matters is what's happening in your house right now.",
      },
    ],
    related: [
      { href: "/sleep-guides/baby-waking-every-2-hours", label: "Guide: why your baby is waking every two hours" },
      { href: "/sleep-guides/baby-30-minute-naps", label: "Guide: why your baby only naps for 30 minutes" },
      { href: "/newborn-sleep-consultant", label: "The earlier weeks: newborn sleep (0–3 months)" },
      { href: "/toddler-sleep-consultant", label: "What comes next: toddler sleep (18+ months)" },
      { href: "/virtual-sleep-consultant", label: "How virtual sleep support works" },
    ],
  },

  "toddler-sleep-consultant": {
    path: "/toddler-sleep-consultant",
    meta: {
      title: "Toddler Sleep Consultant — Bedtime, Night Waking & Early Mornings | The Nurtured Parent Co.",
      description:
        "A toddler sleep consultant for bedtime battles, coming out of the room, night waking, early mornings and nap transitions — for toddlers and young children. Understand what's driving it. Start with a free sleep assessment.",
    },
    label: "Toddler & young child · 18+ months",
    crumb: "Toddler sleep",
    h1: ["Toddler sleep consultant —", "for bedtime, night waking", "and early mornings."],
    hero: [
      "You say goodnight. You get as far as the door. And then: one more book. One more drink. One more hug. Forty-five minutes later you're still in there — and you're not entirely sure when bedtime became a negotiation you keep losing.",
      "Or they fall asleep beautifully, but at 1 a.m. there are footsteps in the hallway. Or 5:12 has somehow become \u201cmorning.\u201d Or the routine that used to work just… stopped.",
      "Okay. Let's talk about that.",
    ],
    sections: [
      {
        h2: "What's usually underneath it",
        body: [
          "Toddlers are not bad sleepers. A toddler asking for one more book is doing exactly what a toddler's brain is built to do — the day was interesting, you're in it, and sleep means leaving all of that. Bedtime resistance is usually some mix of timing, habit, and a very reasonable desire not to miss anything.",
          "Which is why the answer is rarely \u201cbe stricter.\u201d It's figuring out which part is driving yours — the schedule, the pattern of how bedtime ends, the big change that happened last month — and working on that.",
        ],
      },
      {
        h2: "What I'd want to know",
        list: [
          "What happens after you say goodnight — step by step?",
          "How long has bedtime been stretching, and when did it start?",
          "When they come out of their room, what happens next?",
          "Did anything change around the time this started — a new sibling, the crib-to-bed move, a trip, a new school?",
          "What does the hour before bedtime look like?",
        ],
        body: [
          "A baby who wakes during every crib transfer and a toddler who keeps appearing in the hallway can both get called \u201ca bedtime problem.\u201d They usually don't need the same answer. That's the part worth figuring out first.",
        ],
      },
      {
        h2: "How I work with toddler sleep",
        body: [
          "Your child is old enough to be part of this now — and that changes everything. We build something your toddler can understand and you can hold warmly and consistently: clear edges, a bedtime that means something, and a plan for the footsteps at 1 a.m. that doesn't unravel by Thursday.",
          "The plan fits your child's temperament and your family's real evenings — not an idealized version of them. And we adjust it together as it meets actual reality.",
        ],
      },
      {
        h2: "What evenings can look like",
        list: [
          "\u201cGoodnight\u201d actually ends the day.",
          "You walk out of the room without holding your breath.",
          "You and your partner actually finish dinner.",
          "And when it wobbles — a trip, a tooth, a weird week — you know what you're looking at, and what to do.",
        ],
      },
    ],
    faq: [
      {
        q: "Did we wait too long to change this?",
        a: "No. Toddler and young-child sleep is changeable at any age — the approach just has to fit a child who talks, negotiates, and remembers. That's exactly what it's designed for.",
      },
      {
        q: "Will this mean just shutting the door?",
        a: "No. What I suggest depends on your child's temperament and what you're comfortable holding consistently. You'll know your options — and nothing gets suggested that you can't actually live with.",
      },
      {
        q: "We moved them to a bed and everything fell apart.",
        a: "That's one of the most common stories I hear. The move itself is rarely the whole problem — it's usually timing plus what bedtime had quietly become beforehand. Both are fixable.",
      },
    ],
    related: [
      { href: "/sleep-guides/toddler-fighting-bedtime", label: "Guide: when your toddler fights bedtime" },
      { href: "/sleep-guides/early-morning-waking", label: "Guide: early morning waking" },
      { href: "/baby-sleep-consultant", label: "The earlier stage: baby sleep (4–17 months)" },
      { href: "/virtual-sleep-consultant", label: "How virtual sleep support works" },
      { href: "/denver-sleep-consultant", label: "Working together in the Denver area" },
    ],
  },

  "virtual-sleep-consultant": {
    path: "/virtual-sleep-consultant",
    meta: {
      title: "Virtual Pediatric Sleep Consultant for Families Nationwide | The Nurtured Parent Co.",
      description:
        "A virtual pediatric sleep consultant for newborns, babies and toddlers — personalized, one-on-one online sleep consulting wherever you live in the U.S. You don't need to be near Denver. Start with a free sleep assessment.",
    },
    label: "Virtual sleep support · nationwide",
    crumb: "Virtual sleep support",
    h1: ["Virtual pediatric sleep consultant —", "for families", "nationwide."],
    hero: [
      "The fair question first: can someone actually help with your child's sleep without ever stepping into your house?",
      "Honestly — yes. Because sleep happens at home. At 2 a.m., in your real rooms, inside your real routines. That's exactly where the useful information lives. And you're already the one who has all of it.",
    ],
    sections: [
      {
        h2: "What virtual sleep consulting actually is",
        body: [
          "It isn't a video call instead of \u2018real\u2019 help, and it isn't a downloadable plan with your child's name on it. It's personalized pediatric sleep consulting — the same discovery, the same one-on-one guidance — delivered where sleep actually happens: your home.",
          "Some families find me while searching for an online sleep consultant and quietly wonder if they're settling for something less. You're not. When the plan is built around your child and someone experienced is reading your nights with you, the screen between us stops mattering.",
        ],
        andrea:
          "The most important part of sleep support doesn't happen in my office. It happens in your home — in the moments when you're actually putting the plan into practice.",
      },
      {
        h2: "How it actually works",
        body: [
          "We start with a conversation — the complimentary pediatric sleep assessment. You tell me what last night looked like: what happened at bedtime, what the night did, what naps were like. I ask questions. A lot of them, at first: when did this start, what happens when you put them down, what did you try, what happened when you tried it.",
          "If we decide to work together, we go deeper — your child's current sleep patterns, your family's real schedule, the specific challenges you're up against. Then we build a plan around your child, not a template with your child's name on it. And you don't implement it alone: I'm with you while the changes are actually happening at home.",
        ],
      },
      {
        h2: "How I understand what's happening without being in the room",
        body: ["Here's what I learn before I suggest anything:"],
        list: [
          "What sleep actually looks like right now — nights, naps, and bedtime, specifically.",
          "What you've already tried, and whether any of it worked, even briefly.",
          "How your child settles — and what happens when they can't.",
          "Where you're getting stuck, and what the hardest moment of the day is for you.",
          "What you're comfortable changing — and what you're not.",
        ],
      },
      {
        h2: "When the night doesn't follow the plan",
        body: [
          "Children don't read plans. A nap goes sideways, a tooth comes in, a week that was working suddenly isn't. That isn't failure — it's information.",
          "You tell me what happened, we work out which part matters, and the plan adjusts. Naps, bedtime, night waking, schedules — each is handled exactly the way it would be in person: look at what actually happened, then decide together. Two families can describe the same broken night and need completely different thinking; the difference lives in the details, and the details are the job.",
        ],
      },
      {
        h2: "You don't need to live in Colorado",
        body: [
          "I'm based in Denver, but sleep support was never really about being in the room. I work with families across the country, one on one — and the work happens in your home, on your nights, either way.",
          "Every stage can be supported virtually: newborns in their first weeks, babies in the middle of a regression, toddlers negotiating bedtime. And if you're in the Denver area and being there in person would genuinely help — some situations really do benefit from me seeing the room — in-home support may also be available. Far fewer situations need it than parents assume, and if yours is one of them, I'll tell you.",
        ],
        links: [
          { href: "/denver-sleep-consultant", label: "Denver-area sleep support" },
          { href: "/denver-in-home-sleep-consultant", label: "In-home sleep support (Denver area)" },
        ],
      },
    ],
    proof: {
      quote:
        "We reached out to Andrea because our baby was struggling with independent sleep, our baby was sleeping alone in basinet for stretches until 4 months when I started back at work. The long nights and constant feeds made it nearly impossible for us to get any rest. Andrea provided us with a sleep plan that was easy to follow and tailored to our baby's needs. Within a few weeks, we saw significant improvements, and our baby was sleeping through the night! We are so grateful for her help!",
      name: "Brianna Talley",
    },
    faq: [
      {
        q: "What ages can you help virtually?",
        a: "All of them — newborns in their first weeks, babies in the middle of regressions, toddlers and young children negotiating bedtime. The stage changes the plan, not the format.",
      },
      {
        q: "When would in-home support be the better choice?",
        a: "If you're in the Denver area and there's something I'd genuinely understand better by seeing it — the room, the routine, bedtime as it happens — in-home support may be available. For most families and most sleep struggles, virtual covers everything. If I think your situation is the exception, I'll say so.",
      },
      {
        q: "Do we need any special equipment?",
        a: "No. A phone or a computer for our calls, and your own honest account of what's happening at home. That's the whole setup.",
      },
      {
        q: "What if we're not sure our problem is 'big enough'?",
        a: "That's exactly what the complimentary assessment is for. Tell me what's been happening and I'll tell you what I notice — if it's something a small adjustment can handle, I'll say so.",
      },
      {
        q: "How is this different from a sleep course or a book?",
        a: "A course can tell you what usually works. It can't ask you what happened last night — and it can't notice that the part worth changing happened four hours before bedtime.",
      },
    ],
    related: [
      { href: "/sleep-guides/how-virtual-sleep-consulting-works", label: "Guide: how virtual sleep consulting works" },
      { href: "/newborn-sleep-consultant", label: "Newborn sleep (0–3 months)" },
      { href: "/baby-sleep-consultant", label: "Baby sleep (4–17 months)" },
      { href: "/toddler-sleep-consultant", label: "Toddler sleep (18+ months)" },
      { href: "/colorado-pediatric-sleep-consultant", label: "Colorado families" },
      { href: "/denver-sleep-consultant", label: "In the Denver area" },
    ],
  },

  "denver-sleep-consultant": {
    path: "/denver-sleep-consultant",
    meta: {
      title: "Denver Sleep Consultant | The Nurtured Parent Co.",
      description:
        "A pediatric sleep consultant serving Denver-area families — newborn, baby and toddler sleep support, with virtual consulting available everywhere. Start with a free sleep assessment.",
    },
    label: "Denver, Colorado · and beyond",
    crumb: "Denver sleep support",
    h1: ["Pediatric sleep consultant,", "serving Denver families."],
    hero: [
      "There is no shortage of sleep advice out there — books, blogs, well-meaning relatives, and roughly ten thousand conflicting opinions available at 2 a.m. What's harder to find is someone who'll sit down with what last night actually looked like in your house.",
      "I'm Andrea. I'm based in Denver, and before I trained as a pediatric sleep consultant, I spent twenty years caring for children in real homes — as a private nanny, an in-home daycare provider, and a school behavioral interventionist. Newborns through preschool. Real rooms, real routines, real families.",
    ],
    sections: [
      {
        h2: "Working together, from here or anywhere",
        body: [
          "Most of the families I work with — including the Denver-area ones — work with me virtually. Sleep happens at home, on your actual nights, so that's where the work happens too: on a call, one on one, looking at what's really going on.",
          "If you searched for a pediatric sleep consultant near me from anywhere in the Denver area — you're in the right place. In-person support may be available when it would genuinely help, and we can talk about it when we talk. Either way, nothing about the approach changes: I want to understand what's happening before anyone starts changing it.",
        ],
        links: [
          { href: "/denver-in-home-sleep-consultant", label: "In-home sleep support in the Denver area" },
        ],
      },
      {
        h2: "However your nights look right now",
        body: ["Wherever you are in it, there's a page here for your stage:"],
        list: [
          "Newborns (0–3 months) — what's normal, day/night confusion, contact sleep, and gentle early foundations.",
          "Babies (4–17 months) — night waking, short naps, the crib transfer, and why things suddenly change.",
          "Toddlers & young children (18+ months) — bedtime negotiations, coming out of the room, and early mornings.",
        ],
        links: [
          { href: "/newborn-sleep-consultant", label: "Newborn sleep support" },
          { href: "/baby-sleep-consultant", label: "Baby & infant sleep support" },
          { href: "/toddler-sleep-consultant", label: "Toddler & young child sleep" },
          { href: "/virtual-sleep-consultant", label: "Virtual sleep consulting, from anywhere" },
        ],
      },
      {
        h2: "What stays the same wherever you live",
        body: [
          "I ask before I assume. We look at the whole day, not just the loud part of the night. The plan fits your child and your family — not a method you have to fit into. And we adjust together as real life happens.",
          "The first step is just a conversation. Tell me what happened last night — we'll start there.",
        ],
      },
    ],
    faq: [
      {
        q: "Do you only work with Denver families?",
        a: "No. Most of my work is virtual, and families work with me from wherever they live. Being Denver-based simply means local families have one more option available to them.",
      },
      {
        q: "Do you offer in-home visits?",
        a: "In-person support may be available for Denver-area families — we'll talk about what would actually help in your situation. For most families, virtual support covers everything.",
      },
      {
        q: "Are you going to tell me to let my baby cry it out?",
        a: "No. I don't fit families into a predetermined method. What I suggest depends on your child, your circumstances, and what you're comfortable doing night after night. The decision is yours.",
      },
    ],
    related: [
      { href: "/newborn-sleep-consultant", label: "Newborn sleep consultant" },
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/toddler-sleep-consultant", label: "Toddler sleep consultant" },
      { href: "/virtual-sleep-consultant", label: "Virtual sleep consultant" },
      { href: "/denver-in-home-sleep-consultant", label: "In-home sleep consulting (Denver)" },
    ],
  },

  "denver-in-home-sleep-consultant": {
    path: "/denver-in-home-sleep-consultant",
    meta: {
      title: "In-Home Sleep Consultant Denver | The Nurtured Parent Co.",
      description:
        "In-home pediatric sleep consulting for Denver-area families — Andrea works with you inside your real rooms and routines, seeing what the advice can't see. Start with a free sleep assessment.",
    },
    label: "In-home sleep support · Denver area",
    crumb: "In-home sleep support",
    h1: ["In-home sleep consultant —", "for Denver families who'd", "rather have me there."],
    img: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?q=80&w=1400&auto=format&fit=crop",
    imgAlt: "A newborn's feet tucked in a soft blanket at home — the quiet details in-home sleep support is built around",
    hero: [
      "You've read the advice. You've moved bedtime, adjusted the naps, tried putting them down drowsy-but-awake, rocked, fed, waited, searched at 2 a.m. And what happens at 7:30 in your house still doesn't look much like what the advice describes.",
      "That gap — between the advice and your actual evenings — is sometimes the whole problem. And it's the part someone can only really see by being there.",
      "And when the nights themselves are the hard part, overnight support is possible too — I'm there for the night itself, not just the plan around it.",
    ],
    sections: [
      {
        h2: "What being there shows me",
        body: [
          "On a call, you describe the house. In the house, I can see it — and the details that never make it into a description are often the ones that matter.",
          "Working with you in your own environment gives us a different kind of context:",
        ],
        list: [
          "How bedtime actually unfolds, step by step — not how it's supposed to.",
          "The sleep environment itself: light, sound, the room, the crib or bed.",
          "How settling goes, and what happens after a waking.",
          "The interactions around sleep — what your child asks for, what you do, what happens next.",
          "Where the routine quietly starts to break down, and what you've already tried, up close.",
          "What feels realistic for your family, in your actual space.",
        ],
      },
      {
        h2: "In-home and virtual — different, not better and worse",
        body: [
          "Most families I work with — including most Denver families — work with me virtually, and it works beautifully. Sleep happens at home either way, and the conversation is the same one.",
          "But circumstances differ. Sometimes the environment itself might be part of what's happening. Sometimes you've tried the advice carefully and can't see why it isn't landing. Sometimes you simply want someone beside you the first nights of a new plan. Those are the situations where being in the room earns its place — and if you're in the Denver area, it's an option we can talk about.",
        ],
      },
      {
        h2: "What stays the same",
        body: [
          "However we work together, the approach doesn't change. I ask before I assume. We look at the whole day, not just the loud part of the night. The plan is built around your child and your family — not a method you have to fit into — and we adjust it together as real nights push back.",
          "And the same line holds here as everywhere: nothing medical. If anything ever feels medically off, your pediatrician comes first — I'll say that plainly.",
        ],
      },
      {
        h2: "How it starts",
        body: [
          "The same way everything starts here: with a conversation. Tell me what happened last night — what bedtime looked like, what the night did, what you've tried.",
          "If in-home support fits your situation and you're in the Denver area, I'll explain what working together that way could look like. Then you decide.",
        ],
      },
    ],
    faq: [
      {
        q: "Who is in-home support for?",
        a: "Denver-area families where being in the room would genuinely help — or who simply prefer it. Whether it fits your situation is exactly the kind of thing the complimentary assessment is for.",
      },
      {
        q: "Is in-home better than virtual?",
        a: "No — different, not better. Virtual support is how most families work with me and it works beautifully. In-home simply adds context that a call can't always capture, for the situations where that context matters.",
      },
      {
        q: "What does an in-home visit actually involve?",
        a: "That depends on your family and what you're working through — the shape of it is something we decide together after the assessment, not before. What I can tell you now: it centers on your real routines, in your real rooms, at the times of day that matter.",
      },
      {
        q: "Are you going to tell me to let my baby cry it out?",
        a: "No. I don't fit families into a predetermined method. What I suggest depends on your child, your circumstances, and what you're comfortable doing night after night. The decision is yours.",
      },
    ],
    related: [
      { href: "/denver-sleep-consultant", label: "Pediatric sleep consultant serving Denver" },
      { href: "/virtual-sleep-consultant", label: "Virtual sleep support, from anywhere" },
      { href: "/newborn-sleep-consultant", label: "Newborn sleep (0–3 months)" },
      { href: "/baby-sleep-consultant", label: "Baby sleep (4–17 months)" },
      { href: "/toddler-sleep-consultant", label: "Toddler sleep (18+ months)" },
    ],
  },
};

// The two authority pages (national category + Colorado) live in
// authorityPages.js and merge in here — one SLEEP_PAGES map for the renderer.
Object.assign(SLEEP_PAGES, AUTHORITY_PAGES);

// Header photography — the same vetted editorial images already used
// elsewhere on the site (stage photos, hero, Andrea's portrait), so the
// pages feel like one continuous home. Lazy-loaded, descriptive alts.
Object.assign(SLEEP_PAGES["newborn-sleep-consultant"], {
  img: "https://images.unsplash.com/photo-1770831208055-f373ff919753?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwYXJlbnQlMjBjb21mb3J0aW5nJTIwYmFieSUyMGluJTIwY3JpYnxlbnwwfHx8fDE3ODc5Mzk2NjN8MA&ixlib=rb-4.1.0&q=85&w=1400",
  imgAlt: "A mother holding her newborn close beside the crib",
});
Object.assign(SLEEP_PAGES["baby-sleep-consultant"], {
  img: "https://images.pexels.com/photos/35119991/pexels-photo-35119991.jpeg?auto=compress&cs=tinysrgb&w=1400",
  imgAlt: "A parent holding their sleeping baby in a quiet room",
});
Object.assign(SLEEP_PAGES["toddler-sleep-consultant"], {
  img: "https://images.unsplash.com/photo-1530455623052-e8441ae826f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHx0b2RkbGVyJTIwYmVkdGltZSUyMHJvdXRpbmV8ZW58MHx8fHwxNzg3OTM5NjYzfDA&ixlib=rb-4.1.0&q=85&w=1400",
  imgAlt: "A young child finally asleep after a long bedtime",
});
Object.assign(SLEEP_PAGES["virtual-sleep-consultant"], {
  img: "https://images.pexels.com/photos/18649620/pexels-photo-18649620.jpeg?auto=compress&cs=tinysrgb&w=1400",
  imgAlt: "A parent and child together at home — where virtual sleep support actually happens",
});
Object.assign(SLEEP_PAGES["denver-sleep-consultant"], {
  img: "/andrea-portrait.webp",
  imgAlt: "Andrea Hurley, pediatric sleep consultant serving Denver families",
});
